import { Alert, AlertDescription, Badge, Button, cn, Input, Label, Textarea } from '@design-system';
import { AdditionalSettingsButton, useInviteClientStore } from '@/features/invite-client';

import { AlertTriangle, Check, Loader2, Pencil, Send } from 'lucide-react';

import { useState } from 'react';

interface EmailTabProps {
  showHeader?: boolean;
  showAdditionalSettings?: boolean;
}

export function EmailTab({ showHeader = true, showAdditionalSettings = true }: EmailTabProps) {
  const {
    inviteClientConfig,
    isEmailValid,
    isAdvisorEmail,
    canContinueSingle,
    isLoading,
    handleSendEmail,
    handleConfigChange,
  } = useInviteClientStore();

  const [isEditing, setIsEditing] = useState(false);

  const { email, firstName, lastName, emailBody } = inviteClientConfig;

  return (
    <div className="space-y-6">
      {showHeader && (
        <div>
          <h4 className="text-base font-semibold text-gray-950">Send an email invitation</h4>
          <p className="text-foreground text-sm">
            We'll send a proven email template so you don't have to write one from scratch.
          </p>
        </div>
      )}

      <fieldset disabled={isLoading}>
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="firstName">First name (optional)</Label>
              <Input
                id="firstName"
                type="text"
                placeholder="Jane"
                value={firstName}
                onChange={({ target }) => handleConfigChange('firstName', target.value)}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="lastName">Last name (optional)</Label>
              <Input
                id="lastName"
                type="text"
                placeholder="Doe"
                value={lastName}
                onChange={({ target }) => handleConfigChange('lastName', target.value)}
              />
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="email">Email address</Label>
            <Input
              id="email"
              type="email"
              placeholder="client@example.com"
              value={email}
              onChange={({ target }) => handleConfigChange('email', target.value)}
              className={cn({
                'text-destructive': email.length > 0 && !isEmailValid,
                'border-yellow-600': isAdvisorEmail,
              })}
            />
            {isAdvisorEmail && (
              <Alert variant="warning">
                <AlertTriangle />
                <AlertDescription>
                  This email is already associated with your account. Each Waterlily account needs a
                  unique email, so try using a personal email instead.
                </AlertDescription>
              </Alert>
            )}
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="cursor-default">Email template</Label>
              <Badge
                variant="secondary"
                className="cursor-pointer"
                onClick={() => !isLoading && setIsEditing(!isEditing)}
              >
                {isEditing ? (
                  <>
                    <Check className="size-3" />
                    <span className="pr-1">Save</span>
                  </>
                ) : (
                  <>
                    <Pencil className="size-3" />
                    <span className="pr-1">Edit</span>
                  </>
                )}
              </Badge>
            </div>
            {isEditing ? (
              <Textarea
                value={emailBody}
                onChange={({ target }) => handleConfigChange('emailBody', target.value)}
                rows={6}
                className="text-sm"
              />
            ) : (
              <div className="bg-muted rounded-lg p-3">
                <p className="text-foreground line-clamp-3 text-sm">"{emailBody}"</p>
              </div>
            )}
          </div>
        </div>
      </fieldset>

      <div className="flex gap-3">
        {showAdditionalSettings && <AdditionalSettingsButton className="flex-1" />}
        <Button
          className={showAdditionalSettings ? 'flex-1' : 'w-full'}
          onClick={() => handleSendEmail(false)}
          disabled={!canContinueSingle || isLoading}
        >
          {isLoading ? <Loader2 className="animate-spin" /> : <Send />}
          {isLoading ? 'Sending...' : 'Send invitation'}
        </Button>
      </div>
    </div>
  );
}
